import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import { createQuoteRequest } from '../../api/quotes';
import { uploadOfferingImage } from '../../api/offerings';
import AdventureDateTimePicker from '../../Components/Shared/AdventureDateTimePicker';
import CustomDropdown from '../../Components/Shared/CustomDropdown';
import '../AdventureShared.css';
import './RequestQuotePage.css';

export default function RequestQuotePage() {
  const { providerId: paramProviderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const providerId = paramProviderId || searchParams.get('providerId') || "prov-1";

  const [description, setDescription] = useState('');
  const [categoryHint, setCategoryHint] = useState('service');
  const [budgetHint, setBudgetHint] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [preferredStartDate, setPreferredStartDate] = useState('');
  const [preferredEndDate, setPreferredEndDate] = useState('');
  const [contactPreference, setContactPreference] = useState('platform');
  const [attachmentUrls, setAttachmentUrls] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (attachmentUrls.length + files.length > 5) {
      alert("You can attach up to 5 files in total.");
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const res = await uploadOfferingImage(file);
        if (res?.url) setAttachmentUrls(prev => [...prev, res.url]);
      } catch (err) {
        console.error("Upload failed for file: " + file.name, err);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      return setError("Please provide a description of what you need.");
    }
    setIsSubmitting(true);
    setError('');

    try {
      await createQuoteRequest({
        providerId,
        description,
        categoryHint,
        budgetHint: budgetHint ? parseFloat(budgetHint) : 0,
        currency,
        preferredStartDate,
        preferredEndDate,
        attachmentUrls,
        contactPreference
      });
      setSubmittedSuccess(true);
    } catch (err) {
      console.error(err);
      setError("Failed to submit quote request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="RequestQuotePage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>
        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>

          <main className="adv-page-main">
            <div className="adv-page-header">
              <h1 className="adv-page-title">Request a <span className="adv-page-title-accent">Custom Quote</span></h1>
              <p className="adv-page-sub">Describe your tailored project or service request and get custom pricing from the provider.</p>
            </div>

            {submittedSuccess ? (
              <div className="adv-card adv-success-banner" style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h2 className="adv-modal__title">Quote Request Sent!</h2>
                <p className="adv-page-sub" style={{ marginBottom: '24px' }}>
                  Your custom request has been delivered to the provider. You will be notified once they reply with a formal quote.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button className="adv-btn-primary" onClick={() => navigate('/facilitate/quotes')}>
                    View My Quote Requests
                  </button>
                  <button className="adv-btn-outline" onClick={() => navigate('/facilitate/offerings')}>
                    Browse More Offerings
                  </button>
                </div>
              </div>
            ) : (
              <form className="adv-card adv-form-container" onSubmit={handleSubmit}>
                {error && <div className="adv-state-error" style={{ marginBottom: '20px' }}>{error}</div>}

                {/* Section 1: Description */}
                <div className="adv-form-section">
                  <h3 className="adv-form-section-title">1. Project Description</h3>
                  <div className="adv-modal__field">
                    <label className="adv-modal__label">What do you need? *</label>
                    <textarea
                      required
                      rows="5"
                      maxLength={1000}
                      placeholder="Describe your requirements, scope, specific dates, or any special requests in detail..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="adv-modal__textarea"
                    />
                    <span className="adv-modal__char-count">{1000 - description.length} characters remaining</span>
                  </div>

                  <div className="adv-form-grid-2">
                    <div className="adv-modal__field">
                      <label className="adv-modal__label">Category Hint</label>
                      <CustomDropdown
                        value={categoryHint}
                        onChange={setCategoryHint}
                        options={[
                          { value: 'service', label: 'Service' },
                          { value: 'event', label: 'Event' },
                          { value: 'product', label: 'Product' },
                          { value: 'custom', label: 'Custom Work' }
                        ]}
                      />
                    </div>

                    <div className="adv-modal__field">
                      <label className="adv-modal__label">Contact Preference</label>
                      <CustomDropdown
                        value={contactPreference}
                        onChange={setContactPreference}
                        options={[
                          { value: 'platform', label: 'Platform Messages' },
                          { value: 'email', label: 'Email Notification' },
                          { value: 'phone', label: 'Phone / Call' }
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Budget & Timeline */}
                <div className="adv-form-section" style={{ marginTop: '24px' }}>
                  <h3 className="adv-form-section-title">2. Budget & Timeline</h3>
                  <div className="adv-form-grid-2">
                    <div className="adv-modal__field">
                      <label className="adv-modal__label">Approximate Budget ($0 = Not sure)</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ width: '110px', flexShrink: 0 }}>
                          <CustomDropdown
                            value={currency}
                            onChange={setCurrency}
                            options={[
                              { value: 'USD', label: 'USD' }
                            ]}
                          />
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="e.g. 500"
                          value={budgetHint}
                          onChange={(e) => setBudgetHint(e.target.value)}
                          className="adv-modal__input"
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>
                    <div></div>
                  </div>

                  <div className="adv-form-grid-2" style={{ marginTop: '12px' }}>
                    <div className="adv-modal__field">
                      <AdventureDateTimePicker
                        type="date"
                        label="Preferred Start Date"
                        value={preferredStartDate}
                        onChange={setPreferredStartDate}
                        minDate={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="adv-modal__field">
                      <AdventureDateTimePicker
                        type="date"
                        label="Preferred End Date (Optional)"
                        value={preferredEndDate}
                        onChange={setPreferredEndDate}
                        minDate={preferredStartDate || new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Attachments */}
                <div className="adv-form-section" style={{ marginTop: '24px' }}>
                  <h3 className="adv-form-section-title">3. Attachments & Files</h3>
                  <div className="adv-modal__field">
                    <label className="adv-modal__label">Attach relevant specifications or images (Up to 5 files)</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      id="quote-file-input"
                    />
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <label htmlFor="quote-file-input" className="adv-btn-outline" style={{ cursor: 'pointer' }}>
                        📎 Choose Files
                      </label>
                      {uploading && <span className="adv-uploading-text">Uploading files...</span>}
                    </div>

                    {attachmentUrls.length > 0 && (
                      <div className="adv-evidence-grid" style={{ marginTop: '16px' }}>
                        {attachmentUrls.map((url, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <img src={url} alt="Attachment" className="adv-evidence-img" />
                            <button
                              type="button"
                              className="adv-evidence-remove"
                              onClick={() => setAttachmentUrls(prev => prev.filter((_, idx) => idx !== i))}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="adv-form-actions" style={{ marginTop: '32px', display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
                  <button type="button" className="adv-btn-ghost" onClick={() => navigate(-1)}>
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="adv-btn-primary" 
                    disabled={isSubmitting || uploading}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="adv-btn-spinner"></span>
                        Sending Request...
                      </>
                    ) : (
                      'Send Request'
                    )}
                  </button>
                </div>
              </form>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
